import React, { useMemo, useState } from "react";
import { Star, Quote, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { LUX_REVIEWS, LUX_REVIEWS_HEADING } from "../mock";
import GoogleG from "./GoogleG";

const Stars = ({ n = 5, size = 14 }) => (
  <div className="flex gap-0.5 text-[#8a6d3b]">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill={i < n ? "currentColor" : "none"} strokeWidth={i < n ? 0 : 1.2} className={i < n ? "" : "text-[#8a6d3b]/40"} />
    ))}
  </div>
);

const ReviewCard = ({ r }) => (
  <div className="bg-[#F4EFE8] border border-[#1A1A1A]/10 rounded-md p-7 flex flex-col h-full shadow-[0_20px_50px_-30px_rgba(26,26,26,0.4)] hover:shadow-[0_28px_60px_-28px_rgba(26,26,26,0.45)] transition-shadow duration-500">
    <div className="flex items-center justify-between mb-4">
      <Quote size={26} className="text-[#8a6d3b]/40" fill="currentColor" strokeWidth={0} />
      <Stars n={r.rating} />
    </div>
    <h4 className="font-serif-display text-[20px] leading-snug font-medium mb-2">{r.title}</h4>
    <p className="text-[14px] text-[#1A1A1A]/70 leading-relaxed flex-1">&ldquo;{r.text}&rdquo;</p>
    <div className="mt-5 pt-4 border-t border-[#1A1A1A]/10 flex items-center justify-between">
      <div>
        <p className="text-[13px] font-medium">{r.name}</p>
        <p className="text-[11px] text-[#1A1A1A]/50">{r.location}</p>
      </div>
      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase text-[#8a6d3b]">
        <BadgeCheck size={14} /> Verified
      </span>
    </div>
  </div>
);

const LuxuryReviews = () => {
  const [page, setPage] = useState(0);
  const perPage = 6;
  const pages = Math.ceil(LUX_REVIEWS.length / perPage);
  const current = useMemo(
    () => LUX_REVIEWS.slice(page * perPage, page * perPage + perPage),
    [page]
  );

  return (
    <section className="py-20 md:py-28 bg-[#EDE6DC]" id="lux-reviews">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[12px] tracking-[0.3em] uppercase text-[#8a6d3b] mb-4">{LUX_REVIEWS_HEADING.eyebrow}</p>
          <h2 className="font-serif-display text-[34px] md:text-[52px] leading-[1.05] font-medium mb-6">
            {LUX_REVIEWS_HEADING.title}
          </h2>
          <div className="inline-flex items-center gap-4 bg-[#F4EFE8] border border-[#1A1A1A]/10 rounded-full px-6 py-3">
            <GoogleG size={26} />
            <div className="flex items-center gap-2">
              <span className="font-serif-display text-[26px] leading-none">{LUX_REVIEWS_HEADING.rating}</span>
              <Stars n={5} size={16} />
            </div>
            <span className="text-[13px] text-[#1A1A1A]/60">
              {LUX_REVIEWS_HEADING.count.toLocaleString()} reviews
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {current.map((r, i) => (
            <ReviewCard key={`${page}-${i}`} r={r} />
          ))}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-center gap-6 mt-12">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-lux h-11 w-11 rounded-full disabled:opacity-30"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-2">
              {[...Array(pages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === page ? "w-8 bg-[#1A1A1A]" : "w-2 bg-[#1A1A1A]/25"
                  }`}
                  aria-label={`Page ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page === pages - 1}
              className="btn-lux h-11 w-11 rounded-full disabled:opacity-30"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LuxuryReviews;
